/**
 * Food log provider scoped to a single calendar date.
 *
 * `FoodLogOnDate.Provider` exposes shared data, mutations, and add-entry state backed by Zero queries.
 */
import { useQuery, useZero } from "@corpuscomplete/zero-data-transformer/react";
import React, {
  createContext,
  type PropsWithChildren,
  useCallback,
  useMemo,
  useState,
} from "react";
import { createId, createIds, safeParseFloat } from "@corpuscomplete/core/utils";
import { FoodLog } from "@corpuscomplete/core/nutrition/food-log";
import { Food } from "@corpuscomplete/core/nutrition/food";
import { NutrientUI } from "../nutrient";
import { type StoreApi, createStore, useStore } from "zustand";
import { Nutrient } from "@corpuscomplete/core/nutrition/nutrient";
import { NutritionProgram } from "@corpuscomplete/core/nutrition/program";
import type { PromiseWithServerResult, Zero } from "@rocicorp/zero";
import { DateTime } from "@corpuscomplete/core-ui/common/datetime";
import type { UserActor } from "@corpuscomplete/core/actor";
import { mutators, queries } from "@corpuscomplete/core/zero";
import { MealPlan } from "@corpuscomplete/core/nutrition/meal-plan";
import { PlannedMeal } from "@corpuscomplete/core/nutrition/planned-meal";
import { TemporalHelpers } from "@corpuscomplete/core/common/date-time";

export namespace FoodLogOnDate {
  export type Context = {
    userId: string;
    date: Temporal.PlainDate;
  };
  const Context = createContext<Context | null>(null);

  export const Provider = ({ children, userId, date }: PropsWithChildren<Context>) => {
    return (
      <Context.Provider
        value={useMemo(
          () => ({
            userId,
            date,
          }),
          [userId, date],
        )}
      >
        {children}
      </Context.Provider>
    );
  };

  export const useContext = () => {
    const context = React.useContext(Context);
    if (!context) {
      throw new Error("Missing FoodLogOnDateContext");
    }
    return context;
  };

  export function useItems(): readonly FoodLog.Item[] {
    const { userId, date } = useContext();
    const timeZone = DateTime.useTimeZone();
    const [entryRows] = useQuery(
      queries.foodLog.onDate({ date: date.toString(), timeZone, userId }),
      { ttl: "1m" },
    );
    const mealPlan = useMealPlan();
    const plannedMeals = usePlannedMeals();
    const sourceContribution = mergeSourceGroupContributions([
      MealPlan.foodLogSourceGroupContribution(mealPlan, date, timeZone),
      PlannedMeal.foodLogSourceGroupContribution(plannedMeals, date, timeZone, userId),
    ]);

    return FoodLog.items(entryRows, sourceContribution);
  }

  export function useFoodItems() {
    const { userId, date } = useContext();
    const timeZone = DateTime.useTimeZone();
    const [entryRows] = useQuery(
      queries.foodLog.onDate({ date: date.toString(), timeZone, userId }),
      { ttl: "1m" },
    );
    return Food.asItems(entryRows);
  }

  export type Mutations = {
    addEntries: (items: Food.Item[], time: Temporal.PlainTime) => PromiseWithServerResult;
    editEntry: (args: Omit<FoodLog.EditEntry, "timeZone">) => PromiseWithServerResult;
    deleteEntry: (entryId: string) => PromiseWithServerResult;
    createSourceGroup: (
      sourceGroupCandidate: FoodLog.SourceGroupCandidate,
    ) => PromiseWithServerResult;
    resetSourceGroup: (sourceGroup: FoodLog.SourceGroup) => PromiseWithServerResult;
    deleteSourceGroup: (sourceGroupId: string) => PromiseWithServerResult;
  };

  export function useMutations(): Mutations {
    const z = useZero();
    const { userId, date } = useContext();
    const timeZone = DateTime.useTimeZone();
    return useMemo(
      () => ({
        addEntries: (items, time) =>
          z.mutate(
            mutators.foodLog.createEntries({
              userId,
              timeAt: TemporalHelpers.zonedDateTime({ date, time, timeZone }).toInstant(),
              timeZone,
              entries: items.map((item) => ({
                id: createId(),
                foodId: item.food.id,
                foodServingId: item.serving.id,
                amount: item.amount,
                timeZone,
              })),
            }),
          ),
        editEntry: (args) => z.mutate(mutators.foodLog.editEntry(args)),
        deleteEntry: (entryId) => z.mutate(mutators.foodLog.deleteEntry({ id: entryId })),
        createSourceGroup: (sourceGroupCandidate) =>
          z.mutate(
            mutators.foodLog.createSourceGroup({
              userId,
              timeZone,
              sourceId: sourceGroupCandidate.sourceId,
              entryIds: createIds(sourceGroupCandidate.items.length),
              id: createId(),
              sourceType: sourceGroupCandidate.sourceType,
              timeAt: sourceGroupCandidate.timeAt,
            }),
          ),
        resetSourceGroup: (sourceGroup) =>
          z.mutate(
            mutators.foodLog.resetSourceGroup({
              id: sourceGroup.id,
              entryIds: createIds(sourceGroup.source?.items.length ?? 0),
            }),
          ),
        deleteSourceGroup: (sourceGroupId) =>
          z.mutate(mutators.foodLog.deleteSourceGroup({ id: sourceGroupId })),
      }),
      [z, userId, date, timeZone],
    );
  }

  export function useProgramDay(): NutritionProgram.Day | undefined {
    const { userId, date } = useContext();
    const [programs] = useQuery(
      queries.nutritionProgram.inDateRange({
        userId,
        dateRange: {
          dateStart: date,
          dateEnd: date,
        },
      }),
      { ttl: "1m" },
    );
    return NutritionProgram.dayForDate(programs, date);
  }

  export function useMealPlan(): MealPlan.Info | undefined {
    const { userId, date } = useContext();
    const [mealPlans] = useQuery(
      queries.mealPlan.publishedInDateRange({
        userId,
        dateRange: {
          dateStart: date,
          dateEnd: date,
        },
      }),
      { ttl: "1m" },
    );
    if (!mealPlans[0]) {
      return undefined;
    }
    if (mealPlans.length > 1) {
      console.warn("Multiple meal plans found for date", mealPlans);
    }
    return MealPlan.asInfo(mealPlans[0]);
  }

  export function usePlannedMeals(): PlannedMeal.Info[] {
    const { userId, date } = useContext();
    const timeZone = DateTime.useTimeZone();
    const [plannedMeals] = useQuery(
      queries.plannedMeal.eventAssignmentsInDateRange({
        userId,
        dateRange: {
          dateStart: date,
          dateEnd: date,
        },
        timeZone,
      }),
      { ttl: "1m" },
    );

    return plannedMeals.map((meal) => PlannedMeal.asInfo(meal));
  }

  export function useLoadingState(): { isLoading: boolean } {
    const { userId, date } = useContext();
    const timeZone = DateTime.useTimeZone();
    const [, mealPlanDetails] = useQuery(
      queries.mealPlan.publishedInDateRange({
        userId,
        dateRange: {
          dateStart: date,
          dateEnd: date,
        },
      }),
      { ttl: "1m" },
    );
    const [, entryDetails] = useQuery(
      queries.foodLog.onDate({ date: date.toString(), timeZone, userId }),
      { ttl: "1m" },
    );
    const [, plannedMealDetails] = useQuery(
      queries.plannedMeal.eventAssignmentsInDateRange({
        userId,
        dateRange: {
          dateStart: date,
          dateEnd: date,
        },
        timeZone,
      }),
      { ttl: "1m" },
    );
    const [, programDetails] = useQuery(
      queries.nutritionProgram.inDateRange({
        userId,
        dateRange: {
          dateStart: date,
          dateEnd: date,
        },
      }),
      { ttl: "1m" },
    );
    return {
      isLoading:
        programDetails.type === "unknown" ||
        entryDetails.type === "unknown" ||
        mealPlanDetails.type === "unknown" ||
        plannedMealDetails.type === "unknown",
    };
  }

  function mergeSourceGroupContributions(
    contributions: readonly FoodLog.SourceGroupContribution[],
  ): FoodLog.SourceGroupContribution {
    return {
      candidates: contributions.flatMap((contribution) => contribution.candidates),
      sources: new Map(contributions.flatMap((contribution) => [...contribution.sources])),
      defaultLabel: "Food log source",
    };
  }

  export async function preload(zero: Zero, { userId }: UserActor, date: Temporal.PlainDate) {
    const timeZone = await DateTime.getTimeZone(zero);
    void zero.run(queries.foodLog.onDate({ date: date.toString(), timeZone, userId }));
    void zero.run(
      queries.mealPlan.publishedInDateRange({
        userId,
        dateRange: {
          dateStart: date,
          dateEnd: date,
        },
      }),
    );
    void zero.run(
      queries.plannedMeal.eventAssignmentsInDateRange({
        userId,
        dateRange: {
          dateStart: date,
          dateEnd: date,
        },
        timeZone,
      }),
    );
    void zero.run(
      queries.nutritionProgram.inDateRange({
        userId,
        dateRange: {
          dateStart: date,
          dateEnd: date,
        },
      }),
    );
  }

  export type AddEntryDialogStore = {
    time: Temporal.PlainTime;
    onChangeTime: (time: Temporal.PlainTime) => void;
    items: FoodItemInput.Info[];
    onAddItem: (item: Food.Item) => void;
    onSelectItem: (item: Food.Item, index: number) => void;
    onEditItemAmount: (index: number, amountInput: string) => void;
    onEditItemServing: (index: number, serving: Food.Serving) => void;
    onRemoveItem: (index: number) => void;
    pending: {
      item: FoodItemInput.Info;
      index: number | null;
    } | null;
    onSetPendingItem: (item: Food.Item) => void;
    onAddPendingItem: () => void;
    onEditPendingItemAmount: (amountInput: string) => void;
    onEditPendingItemServing: (serving: Food.Serving) => void;
    onClearPendingItem: () => void;
    onLog: () => void;
    onLogWithPending: () => void;
  };

  export type AddEntrySession = {
    initialTime: Temporal.PlainTime | null;
    isOpen: boolean;
    open: () => void;
    openAtTime: (time: Temporal.PlainTime) => void;
    close: () => void;
    createEntries: (items: Food.Item[], time: Temporal.PlainTime) => PromiseWithServerResult;
  };

  export function useAddEntrySession(): AddEntrySession {
    const { addEntries } = useMutations();
    const [initialTime, setInitialTime] = useState<Temporal.PlainTime | null>(null);
    const [isOpen, setIsOpen] = useState(false);

    const close = useCallback(() => {
      setInitialTime(null);
      setIsOpen(false);
    }, []);

    const open = useCallback(() => {
      setInitialTime(null);
      setIsOpen(true);
    }, []);

    const openAtTime = useCallback((time: Temporal.PlainTime) => {
      setInitialTime(time);
      setIsOpen(true);
    }, []);

    const createEntries = useCallback<AddEntrySession["createEntries"]>(
      (items, time) => {
        const result = addEntries(items, time);
        close();
        return result;
      },
      [addEntries, close],
    );

    return useMemo(
      () => ({
        initialTime,
        isOpen,
        open,
        openAtTime,
        close,
        createEntries,
      }),
      [initialTime, isOpen, open, openAtTime, close, createEntries],
    );
  }

  const AddEntryDialogContext = createContext<StoreApi<AddEntryDialogStore> | null>(null);

  export const AddEntryDialogProvider = ({
    children,
    onCreateEntries,
    initialTime,
  }: PropsWithChildren<{
    initialTime: Temporal.PlainTime | null;
    onCreateEntries: (items: Food.Item[], time: Temporal.PlainTime) => void;
  }>) => {
    const [store] = useState(() =>
      createStore<AddEntryDialogStore>((set, get) => ({
        time: initialTime ?? Temporal.Now.plainTimeISO(),
        onChangeTime: (time) => set({ time }),
        items: [],
        onSelectItem: (item, index) => {
          set({
            pending: {
              item: FoodItemInput.fromItem(item),
              index,
            },
          });
        },
        onAddItem: (item) => {
          set({
            items: [...get().items, FoodItemInput.fromItem(item)],
          });
        },
        onEditItemAmount: (index, amountInput) => {
          set({
            items: get().items.map((item, i) =>
              i === index ? FoodItemInput.withNewAmount(item, amountInput) : item,
            ),
          });
        },
        onEditItemServing: (index, serving) => {
          set({
            items: get().items.map((item, i) =>
              i === index ? FoodItemInput.withNewServing(item, serving) : item,
            ),
          });
        },
        onRemoveItem: (index) => {
          const { items } = get();
          set({ items: items.filter((_, i) => i !== index) });
        },
        pending: null,
        onSetPendingItem: (item) =>
          set({
            pending: {
              item: FoodItemInput.fromItem(item),
              index: null,
            },
          }),
        onAddPendingItem: () => {
          const { items, pending } = get();
          if (!pending) {
            return;
          }
          let newItems: typeof items;
          if (pending.index === null) {
            newItems = [...items, pending.item];
          } else {
            newItems = items.map((i, index) => (index === pending.index ? pending.item : i));
          }
          set({
            items: newItems,
            pending: null,
          });
        },
        onEditPendingItemAmount: (amountInput) => {
          const { pending } = get();
          if (!pending) {
            return;
          }
          set({
            pending: {
              ...pending,
              item: FoodItemInput.withNewAmount(pending.item, amountInput),
            },
          });
        },
        onEditPendingItemServing: (serving) => {
          const { pending } = get();
          if (!pending) {
            return;
          }
          set({
            pending: {
              ...pending,
              item: FoodItemInput.withNewServing(pending.item, serving),
            },
          });
        },
        onClearPendingItem: () => set({ pending: null }),
        onLog: () => {
          const { pending } = get();
          if (pending) {
            return;
          }
          const { items, time } = get();
          onCreateEntries(items, time);
        },
        onLogWithPending: () => {
          const { pending } = get();
          if (!pending) {
            return;
          }
          if (!FoodItemInput.amountMatchesInput(pending.item)) {
            return;
          }
          const { items, time } = get();
          onCreateEntries([...items, pending.item], time);
        },
      })),
    );

    return (
      <AddEntryDialogContext.Provider value={store}>{children}</AddEntryDialogContext.Provider>
    );
  };

  export const useAddEntryDialogStore = <T extends unknown>(
    selector: (state: AddEntryDialogStore) => T,
  ): T => {
    const store = React.useContext(AddEntryDialogContext);
    if (!store) {
      throw new Error("Missing AddEntryDialogProvider");
    }
    return useStore(store, selector);
  };

  export const useAddEntryDialogNutrientAmounts = () => {
    const foodItems = useFoodItems();
    const addingItems = useAddEntryDialogStore((state) => state.items);
    const pending = useAddEntryDialogStore((state) => state.pending);
    NutrientUI.useDisplayNutrients();

    const result = useMemo(() => {
      return Nutrient.addFoodItems(Nutrient.emptyAmounts(), [
        ...addingItems.filter((_, index) => pending?.index !== index),
        ...foodItems,
      ]);
    }, [addingItems, foodItems, pending?.index]);

    return result;
  };
}

export namespace FoodItemInput {
  export type Info = Food.Item & {
    amountInput: string;
  };

  export function fromItem(item: Food.Item): Info {
    return {
      ...item,
      amountInput: item.amount.toString(),
    };
  }

  export function withNewAmount(current: Info, amountInput: string): Info {
    const newAmount = safeParseFloat(amountInput);
    return {
      ...current,
      amountInput,
      amount: newAmount ?? current.amount,
    };
  }

  export function withNewServing(current: Info, newServing: Food.Serving): Info {
    return {
      ...current,
      serving: newServing,
      amount: newServing.defaultAmount,
      amountInput: newServing.defaultAmount.toString(),
    };
  }

  export function amountMatchesInput(item: Info): boolean {
    return safeParseFloat(item.amountInput) === item.amount;
  }
}
